import React, { useRef, useState } from "react";
import "./collaborationValidation.css";

const rules: Record<string, { label: string; required?: boolean; max?: number }> = {
  nome_especie: { label: "Nome da espécie", required: true, max: 255 },
  data: { label: "Data da coleta", required: true },
  pais: { label: "País", required: true, max: 100 },
  regiao: { label: "Região", required: true, max: 100 },
  estado: { label: "Estado", required: true, max: 100 },
  municipio: { label: "Município", required: true, max: 100 },
  latitude: { label: "Latitude", required: true },
  longitude: { label: "Longitude", required: true },
  altitude: { label: "Altitude" },
  coletor: { label: "Coletor", required: true, max: 255 },
  instituicao_coletor: { label: "Instituição do coletor", required: true, max: 255 },
  autor_foto: { label: "Autor da foto", required: true, max: 255 },
  instituicao_autor: { label: "Instituição do autor da foto", required: true, max: 255 },
  SISBIO: { label: "SISBIO", max: 100 }, SISGEN: { label: "SISGEN", max: 100 },
  observacoes: { label: "Observações" },
};
const aliases: Record<string, string> = {
  speciesName: "nome_especie", collectionDate: "data", country: "pais", region: "regiao",
  state: "estado", city: "municipio", collector: "coletor", collectorInstitution: "instituicao_coletor",
  photoAuthor: "autor_foto", photoAuthorInstitution: "instituicao_autor", sibbio: "SISBIO", sisgen: "SISGEN",
  observations: "observacoes",
};
const keyFor = (name: string) => aliases[name] || name;
export function validateCollaborationField(name: string, value: string) {
  const key = keyFor(name), rule = rules[key];
  if (!rule) return "";
  if (rule.required && !value.trim()) return "Preencha este campo obrigatório.";
  if (rule.max && value.length > rule.max) return `Use no máximo ${rule.max} caracteres.`;
  if (key === "data" && value && (!/^\d{4}-\d{2}-\d{2}$/.test(value) || !Number.isFinite(Date.parse(value)))) return "Informe uma data válida.";
  if (key === "altitude" && value && !/^[+-]?([0-9]*[.])?[0-9]+$/.test(value)) return "Informe um número em metros, usando ponto para decimais (ex.: 120.5).";
  return "";
}
export function validateImageFile(file: File) {
  if (!/^image\/(jpeg|png|webp|gif|avif|bmp|tiff)$/i.test(file.type)) return "Formato não aceito. Use JPG, PNG, WEBP, GIF, AVIF, BMP ou TIFF.";
  if (file.size > 10 * 1024 * 1024) return "A imagem excede 10 MB. Escolha um arquivo menor.";
  return "";
}

export function useCollaborationValidation(data: object, imageCount: number, pendingImage: boolean) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState("");
  const formRef = useRef<HTMLFormElement | null>(null);
  const focusField = (form: HTMLFormElement, name: string) => {
    const target = form.querySelector<HTMLElement>(`[name="${name}"]`) || form.querySelector<HTMLElement>("[data-collaboration-images]");
    target?.focus(); target?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  };
  const values = data as Record<string, string>;
  const change = (name: string, value: string) => {
    setErrors(previous => {
      if (!(name in previous)) return previous;
      const next = { ...previous }, error = validateCollaborationField(name, value);
      if (error) next[name] = error; else delete next[name];
      return next;
    });
    setServerError("");
  };
  const field = (name: string) => ({
    type: keyFor(name) === "altitude" ? "number" : keyFor(name) === "data" ? "date" : "text",
    step: keyFor(name) === "altitude" ? "any" : undefined,
    inputMode: keyFor(name) === "altitude" ? "decimal" as const : undefined,
    required: !!rules[keyFor(name)]?.required,
    maxLength: rules[keyFor(name)]?.max,
    "aria-invalid": !!errors[name],
    "aria-describedby": errors[name] ? `collaboration-error-${name}` : undefined,
    className: `form-control${errors[name] ? " is-invalid" : ""}`,
    onBlur: () => {
      const error = validateCollaborationField(name, values[name] || "");
      setErrors(previous => { const next = { ...previous }; if (error) next[name] = error; else delete next[name]; return next; });
    },
  });
  const errorFor = (name: string) => errors[name] ? <p className="collaboration-field-error" id={`collaboration-error-${name}`}>{errors[name]}</p> : null;
  const validate = (form: HTMLFormElement) => {
    formRef.current = form;
    const next: Record<string, string> = {};
    Object.entries(values).forEach(([name, value]) => { const error = validateCollaborationField(name, value || ""); if (error) next[name] = error; });
    form.querySelectorAll<HTMLInputElement>("input[name]").forEach(input => {
      if (input.validity.badInput) next[input.name] = "Informe um número válido.";
    });
    if (pendingImage) next.images = "Há uma imagem selecionada: clique em Adicionar/Salvar imagem antes de enviar, ou descarte a seleção.";
    else if (!imageCount) next.images = "Adicione pelo menos uma imagem com código antes de enviar.";
    setErrors(next); setServerError("");
    const first = Object.keys(next)[0];
    if (first) {
      focusField(form, first);
    }
    return !first;
  };
  const reportServerError = (error: any) => {
    const status = error?.response?.status;
    const messages: string[] = Array.isArray(error?.response?.data?.errors) ? error.response.data.errors.filter((item: unknown) => typeof item === "string") : [];
    const next: Record<string, string> = {};
    Object.keys(values).forEach(name => {
      if (messages.some(message => message.startsWith(`${keyFor(name)} `))) next[name] = "Verifique o valor informado: ele não foi aceito pelo servidor.";
    });
    setErrors(next);
    if (formRef.current) {
      if (Object.keys(next).length) focusField(formRef.current, Object.keys(next)[0]);
      else formRef.current.scrollIntoView?.({ behavior: "smooth", block: "start" });
    }
    setServerError(status === 401 ? "Sua sessão expirou. Entre novamente antes de enviar." : status === 413 ? "Uma imagem excedeu o tamanho permitido de 10 MB." : status === 400 ? "O servidor não aceitou os dados. Confira os campos indicados e os arquivos das imagens." : status === 409 ? "Já existe um registro com esse código. Confira os códigos das imagens." : "Não foi possível concluir o envio. Seus dados foram mantidos. Verifique a conexão e tente novamente.");
  };
  const reset = () => { setErrors({}); setServerError(""); };
  const summary = Object.keys(errors).length || serverError ? <div className="collaboration-error-summary" role="alert">
    <strong>{serverError || "Confira os campos abaixo antes de enviar:"}</strong>
    {Object.keys(errors).length > 0 && <ul>{Object.entries(errors).map(([name, error]) => <li key={name}>{rules[keyFor(name)]?.label || "Imagens"}: {error}</li>)}</ul>}
  </div> : null;
  const rejectImage = (message: string) => setErrors(previous => ({ ...previous, images: message }));
  return { field, errorFor, change, validate, reportServerError, reset, summary, rejectImage };
}
